/**
 * Task 5 — user-configurable keyboard shortcuts for worksheet row actions.
 *
 * Single registry shared by the row context menu and the grid key handler so
 * the two can never drift: each action id maps to one label + one default
 * combo, and the user's overrides are persisted per-browser-profile under a
 * tenant-neutral `qb.*` localStorage key.
 *
 * Combo string format (canonical, order fixed): `mod+alt+shift+Key`
 * - `mod` = Ctrl on Windows/Linux, Cmd on macOS
 * - single-character keys are lowercased; named keys keep their KeyboardEvent
 *   name (`ArrowUp`, `Backspace`, `Delete`, `Enter`, …)
 *
 * Defaults intentionally use `mod+shift`/`mod+alt` for row and view actions so
 * they can't clobber the grid's cell-level clipboard (mod+C/X/V). Edit actions
 * that ARE the Excel defaults (undo/fill/find) sit on those exact chords so a
 * remap can move them; they live in this registry for the same reason.
 */

export type WorkbookActionGroup = 'Edit' | 'Rows' | 'View' | 'Data' | 'Help' | 'Panels'

export type WorkbookActionId =
  | 'edit.undo'
  | 'edit.redo'
  | 'edit.fillDown'
  | 'edit.fillRight'
  | 'row.copy'
  | 'row.cut'
  | 'row.paste'
  | 'row.duplicate'
  | 'row.insertAbove'
  | 'row.insertBelow'
  | 'row.delete'
  | 'row.walkUp'
  | 'row.walkDown'
  | 'row.indent'
  | 'row.outdent'
  | 'row.pin'
  | 'rows.tickSelect'
  | 'section.add'
  | 'cell.pasteValues'
  | 'cell.bulkApply'
  | 'cell.customRate'
  | 'view.columns'
  | 'view.freeze'
  | 'view.freezeReset'
  | 'view.provenance'
  | 'data.find'
  | 'data.replace'
  | 'data.issues'
  | 'sheet.repeat'
  | 'sheet.extendSelection'
  | 'sheet.recalculate'
  | 'sheet.tips'
  | 'chrome.fullscreen'
  | 'chrome.viewNext'
  | 'chrome.viewPrev'
  | 'chrome.drawer.files'
  | 'chrome.drawer.summary'
  | 'chrome.drawer.metadata'
  | 'chrome.drawer.email'
  | 'chrome.drawer.linked'
  | 'chrome.drawer.notes'
  | 'chrome.drawer.rates'
  | 'chrome.drawer.formulas'
  | 'chrome.drawer.terms'
  | 'chrome.drawer.takeoff'

export interface WorkbookActionDef {
  id: WorkbookActionId
  label: string
  defaultCombo: string
  /** Tips remap list heading — keeps ~30 entries scannable. */
  group: WorkbookActionGroup
}

export const WORKBOOK_ACTIONS: WorkbookActionDef[] = [
  // Edit — Excel defaults, remappable so a button and its chord stay one path.
  { id: 'edit.undo', label: 'Undo', defaultCombo: 'mod+z', group: 'Edit' },
  { id: 'edit.redo', label: 'Redo', defaultCombo: 'mod+shift+z', group: 'Edit' },
  { id: 'row.cut', label: 'Cut row', defaultCombo: 'mod+shift+x', group: 'Edit' },
  { id: 'row.copy', label: 'Copy row', defaultCombo: 'mod+shift+c', group: 'Edit' },
  { id: 'row.paste', label: 'Paste row below', defaultCombo: 'mod+shift+v', group: 'Edit' },
  { id: 'cell.pasteValues', label: 'Paste values only', defaultCombo: 'mod+alt+v', group: 'Edit' },
  { id: 'edit.fillDown', label: 'Fill down', defaultCombo: 'mod+d', group: 'Edit' },
  { id: 'edit.fillRight', label: 'Fill right', defaultCombo: 'mod+r', group: 'Edit' },
  { id: 'sheet.repeat', label: 'Repeat last row action', defaultCombo: 'F4', group: 'Edit' },
  { id: 'cell.bulkApply', label: 'Apply active cell to selection', defaultCombo: 'mod+Enter', group: 'Edit' },
  { id: 'cell.customRate', label: 'Set custom rate', defaultCombo: 'mod+alt+r', group: 'Edit' },

  // Rows
  { id: 'row.insertAbove', label: 'Add row above', defaultCombo: 'mod+alt+ArrowUp', group: 'Rows' },
  { id: 'row.insertBelow', label: 'Add row below', defaultCombo: 'mod+alt+ArrowDown', group: 'Rows' },
  { id: 'row.duplicate', label: 'Duplicate row', defaultCombo: 'mod+shift+d', group: 'Rows' },
  { id: 'row.delete', label: 'Delete row', defaultCombo: 'mod+shift+Backspace', group: 'Rows' },
  { id: 'row.walkUp', label: 'Move product up', defaultCombo: 'alt+shift+ArrowUp', group: 'Rows' },
  { id: 'row.walkDown', label: 'Move product down', defaultCombo: 'alt+shift+ArrowDown', group: 'Rows' },
  { id: 'row.indent', label: 'Indent product', defaultCombo: 'alt+shift+ArrowRight', group: 'Rows' },
  // Outdent is the other half of indent. It reached the sheet via Shift+Tab but
  // never had a registry entry, so it was missing from Tips and could not be
  // remapped — an option offered in one direction only.
  { id: 'row.outdent', label: 'Outdent product', defaultCombo: 'alt+shift+ArrowLeft', group: 'Rows' },
  { id: 'row.pin', label: 'Pin row', defaultCombo: 'mod+alt+p', group: 'Edit' },
  { id: 'rows.tickSelect', label: 'Tick to select', defaultCombo: 'mod+alt+t', group: 'Rows' },
  { id: 'section.add', label: 'Add section', defaultCombo: 'mod+alt+s', group: 'Rows' },

  // View
  { id: 'view.columns', label: 'Show or hide columns', defaultCombo: 'mod+shift+l', group: 'View' },
  { id: 'view.freeze', label: 'Freeze to selection', defaultCombo: 'mod+alt+f', group: 'View' },
  { id: 'view.freezeReset', label: 'Reset freeze', defaultCombo: 'mod+alt+shift+f', group: 'View' },
  { id: 'sheet.extendSelection', label: 'Extend selection mode', defaultCombo: 'F8', group: 'View' },
  { id: 'view.provenance', label: 'Provenance guides', defaultCombo: 'mod+alt+g', group: 'View' },

  // Data
  { id: 'data.find', label: 'Find', defaultCombo: 'mod+f', group: 'Data' },
  { id: 'data.replace', label: 'Replace', defaultCombo: 'mod+shift+h', group: 'Data' },
  { id: 'sheet.recalculate', label: 'Recalculate sheet', defaultCombo: 'F9', group: 'Data' },
  { id: 'data.issues', label: 'Jump to first sheet issue', defaultCombo: 'mod+alt+w', group: 'Data' },

  // Help
  { id: 'sheet.tips', label: 'Tips & shortcuts', defaultCombo: 'F1', group: 'Help' },

  // Page chrome (tabs, fullscreen, icon rail) — package-hosted only.
  { id: 'chrome.fullscreen', label: 'Full screen', defaultCombo: 'mod+shift+f', group: 'Panels' },
  { id: 'chrome.viewNext', label: 'Next sheet view', defaultCombo: 'mod+alt+]', group: 'Panels' },
  { id: 'chrome.viewPrev', label: 'Previous sheet view', defaultCombo: 'mod+alt+[', group: 'Panels' },
  { id: 'chrome.drawer.files', label: 'Files panel', defaultCombo: 'mod+alt+1', group: 'Panels' },
  { id: 'chrome.drawer.summary', label: 'Summary & GP panel', defaultCombo: 'mod+alt+2', group: 'Panels' },
  { id: 'chrome.drawer.metadata', label: 'Add metadata panel', defaultCombo: 'mod+alt+0', group: 'Panels' },
  { id: 'chrome.drawer.email', label: 'Quote Email panel', defaultCombo: 'mod+alt+3', group: 'Panels' },
  { id: 'chrome.drawer.linked', label: 'Linked Emails panel', defaultCombo: 'mod+alt+4', group: 'Panels' },
  { id: 'chrome.drawer.notes', label: 'Notes panel', defaultCombo: 'mod+alt+5', group: 'Panels' },
  { id: 'chrome.drawer.rates', label: 'Rate Settings panel', defaultCombo: 'mod+alt+6', group: 'Panels' },
  { id: 'chrome.drawer.formulas', label: 'Formula Settings panel', defaultCombo: 'mod+alt+7', group: 'Panels' },
  { id: 'chrome.drawer.terms', label: 'Terms & Conditions panel', defaultCombo: 'mod+alt+8', group: 'Panels' },
  { id: 'chrome.drawer.takeoff', label: 'Takeoff panel', defaultCombo: 'mod+alt+9', group: 'Panels' },
]

/**
 * Actions F4 will repeat. Copy/cut are excluded (repeating one changes
 * nothing) and so are the sheet-level actions, which would recurse.
 */
const REPEATABLE = new Set<WorkbookActionId>([
  'row.paste',
  'row.duplicate',
  'row.insertAbove',
  'row.insertBelow',
  'row.delete',
  'cell.pasteValues',
  'cell.bulkApply',
])

export function isRepeatableAction(id: WorkbookActionId): boolean {
  return REPEATABLE.has(id)
}

/** Tenant-neutral per-user (browser profile) persistence key. */
export const SHORTCUTS_STORAGE_KEY = 'qb.workbookShortcuts.v1'

export type ShortcutOverrides = Partial<Record<WorkbookActionId, string>>

const ACTION_IDS = new Set(WORKBOOK_ACTIONS.map((a) => a.id))

export function loadShortcutOverrides(): ShortcutOverrides {
  try {
    const raw = window.localStorage.getItem(SHORTCUTS_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: ShortcutOverrides = {}
    for (const [id, combo] of Object.entries(parsed)) {
      if (ACTION_IDS.has(id as WorkbookActionId) && typeof combo === 'string' && combo) {
        out[id as WorkbookActionId] = combo
      }
    }
    return out
  } catch {
    return {}
  }
}

export function saveShortcutOverrides(overrides: ShortcutOverrides): void {
  try {
    window.localStorage.setItem(SHORTCUTS_STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    // Storage unavailable (private mode) — shortcuts just won't persist.
  }
}

/** The combo currently bound to an action (override wins over default). */
export function effectiveCombo(overrides: ShortcutOverrides, id: WorkbookActionId): string {
  return overrides[id] ?? WORKBOOK_ACTIONS.find((a) => a.id === id)!.defaultCombo
}

/**
 * Normalise a KeyboardEvent into the canonical combo string, or null for a
 * bare modifier press.
 */
export function comboFromEvent(e: {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
  shiftKey: boolean
}): string | null {
  const key = e.key
  if (key === 'Control' || key === 'Meta' || key === 'Shift' || key === 'Alt') return null
  const parts: string[] = []
  if (e.ctrlKey || e.metaKey) parts.push('mod')
  if (e.altKey) parts.push('alt')
  if (e.shiftKey) parts.push('shift')
  parts.push(key.length === 1 ? key.toLowerCase() : key)
  return parts.join('+')
}

/**
 * A combo is bindable only when it carries mod or alt, or is a function key —
 * plain keys and shift+letter must stay free for typing in cell editors, but a
 * function key never types anything. F2 belongs to the grid's own cell editor
 * and the rest are the browser's (F3 find, F5 reload, F11 fullscreen, …).
 */
const FUNCTION_KEYS = new Set(['F1', 'F4', 'F8', 'F9'])

export function isComboAllowed(combo: string): boolean {
  return combo.startsWith('mod+') || combo.startsWith('alt+') || FUNCTION_KEYS.has(combo)
}

/** Which action (if any) the combo triggers, honouring overrides. */
export function resolveWorkbookAction(
  overrides: ShortcutOverrides,
  combo: string,
): WorkbookActionId | null {
  for (const a of WORKBOOK_ACTIONS) {
    if (effectiveCombo(overrides, a.id) === combo) return a.id
  }
  return null
}

/** True when the action id belongs to the page chrome (tabs / drawers). */
export function isChromeAction(id: WorkbookActionId): boolean {
  return id.startsWith('chrome.')
}

/** Human-readable combo for the Tips panel (⌘⇧C on mac, Ctrl+Shift+C elsewhere). */
export function formatCombo(combo: string, isMac: boolean): string {
  const KEY_LABELS: Record<string, string> = {
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Backspace: '⌫',
  }
  const parts = combo.split('+')
  const mapped = parts.map((p) => {
    if (p === 'mod') return isMac ? '⌘' : 'Ctrl'
    if (p === 'shift') return isMac ? '⇧' : 'Shift'
    if (p === 'alt') return isMac ? '⌥' : 'Alt'
    return KEY_LABELS[p] ?? (p.length === 1 ? p.toUpperCase() : p)
  })
  return mapped.join(isMac ? '' : '+')
}
